import React, { useState, useEffect, useCallback } from 'react';
import { getPositions, exitPosition, exitLivePosition, exitAllPositions } from '../../api/client';

const TABS = ['ALL', 'OPEN', 'CLOSED'];

function fmt(n) { return (n || 0).toFixed(2); }

function timeStr(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function dateStr(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function Positions() {
  const [positions, setPositions]   = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [exiting, setExiting]         = useState(false);
  const [exitingId, setExitingId]     = useState(null);   // trade id being exited

  // Filters
  const [tab, setTab]           = useState('OPEN');
  const [filterStrat, setFilterStrat] = useState('');
  const [filterSide, setFilterSide]   = useState('');
  const [showHistory, setShowHistory] = useState(false);  // CLOSED tab: all-time history

  const fetchPositions = useCallback(async () => {
    try {
      const params = { status: tab };
      if (filterStrat) params.strategy = filterStrat;
      if (filterSide)  params.side     = filterSide;
      if (tab === 'CLOSED' && showHistory) params.all_history = 'true';
      const res = await getPositions(params);
      setPositions(res.data.positions || []);
      setStrategies(res.data.strategies || []);
    } catch {}
    finally { setLoading(false); }
  }, [tab, filterStrat, filterSide, showHistory]);

  useEffect(() => {
    setLoading(true);
    fetchPositions();
    const t = setInterval(fetchPositions, 5000);
    return () => clearInterval(t);
  }, [fetchPositions]);

  const handleExitAll = async () => {
    if (!window.confirm('Exit ALL open positions? This cannot be undone.')) return;
    setExiting(true);
    try { await exitAllPositions(); await fetchPositions(); } catch {}
    setExiting(false);
  };

  const handleExitOne = async (p) => {
    if (!window.confirm(`Exit ${p.symbol?.replace(/^(NSE:|BSE:)/, '')} @ market price?`)) return;
    // Use symbol as key for live positions (no numeric DB id), trade id for paper
    const exitKey = p.paper_trade ? p.id : p.symbol;
    setExitingId(exitKey);
    try {
      if (p.paper_trade) {
        await exitPosition(p.id);
      } else {
        await exitLivePosition(p.symbol);
      }
      await fetchPositions();
    } catch {}
    setExitingId(null);
  };

  const exportCsv = () => {
    if (!positions.length) return;
    // Export exactly what's currently loaded (already filtered by tab / strategy
    // / side / history via the API params).
    const cols = [
      ['Symbol',     p => p.symbol],
      ['Strategy',   p => p.strategy],
      ['Side',       p => p.side],
      ['Qty',        p => p.qty],
      ['Entry',      p => fmt(p.avgPrice)],
      ['Exit',       p => (p.exitPrice != null ? fmt(p.exitPrice) : '')],
      ['LTP',        p => (p.ltp != null ? fmt(p.ltp) : '')],
      ['P&L',        p => fmt(p.pl)],
      ['Stop Loss',  p => (p.stopLoss != null ? fmt(p.stopLoss) : '')],
      ['Target',     p => (p.target != null ? fmt(p.target) : '')],
      ['Entry Time', p => p.entryTime || ''],
      ['Exit Time',  p => p.exitTime || ''],
      ['Status',     p => p.status],
    ];
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      cols.map(c => c[0]).join(','),
      ...positions.map(p => cols.map(c => esc(c[1](p))).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positions_${tab.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openPositions  = positions.filter(p => p.status === 'OPEN');
  const closedPositions = positions.filter(p => p.status === 'CLOSED');

  const openPnl   = openPositions.reduce((s, p) => s + (p.pl || 0), 0);
  const closedPnl = closedPositions.reduce((s, p) => s + (p.pl || 0), 0);
  const totalPnl  = positions.reduce((s, p) => s + (p.pl || 0), 0);

  const displayedPnl = tab === 'OPEN' ? openPnl : tab === 'CLOSED' ? closedPnl : totalPnl;
  const pnlLabel     = tab === 'OPEN' ? 'MTM' : tab === 'CLOSED' ? 'Realised' : 'Total P&L';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Positions</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: displayedPnl >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: 15 }}>
            {pnlLabel}: ₹{displayedPnl.toFixed(2)}
          </span>
          {tab !== 'CLOSED' && (
            <button
              className="btn btn-danger"
              onClick={handleExitAll}
              disabled={exiting || openPositions.length === 0}
            >
              {exiting ? 'Exiting...' : '⚡ Exit All'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs + Filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', borderRadius: 8, padding: 4 }}>
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '5px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                background: tab === t ? 'var(--accent)' : 'transparent',
                color: tab === t ? '#fff' : 'var(--muted)',
                transition: 'all 0.15s',
              }}
            >
              {t}
              {t === 'OPEN'   && openPositions.length   > 0 && (
                <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                  {openPositions.length}
                </span>
              )}
              {t === 'CLOSED' && closedPositions.length > 0 && (
                <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                  {closedPositions.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Strategy filter */}
        {strategies.length > 0 && (
          <select
            value={filterStrat}
            onChange={e => setFilterStrat(e.target.value)}
            style={{
              background: 'var(--surface)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '5px 10px', fontSize: 13, cursor: 'pointer',
            }}
          >
            <option value="">All Strategies</option>
            {strategies.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {/* Side filter */}
        <select
          value={filterSide}
          onChange={e => setFilterSide(e.target.value)}
          style={{
            background: 'var(--surface)', color: 'var(--text)',
            border: '1px solid var(--border)', borderRadius: 6,
            padding: '5px 10px', fontSize: 13, cursor: 'pointer',
          }}
        >
          <option value="">All Sides</option>
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
        </select>

        {(filterStrat || filterSide) && (
          <button
            onClick={() => { setFilterStrat(''); setFilterSide(''); }}
            style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Clear filters
          </button>
        )}

        {/* Export current view to CSV */}
        <button
          onClick={exportCsv}
          disabled={!positions.length}
          title={positions.length ? 'Download the current view as CSV' : 'No rows to export'}
          style={{
            marginLeft: 'auto',
            padding: '5px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: positions.length ? 'var(--muted)' : '#555',
            fontSize: 12,
            fontWeight: 600,
            cursor: positions.length ? 'pointer' : 'not-allowed',
            whiteSpace: 'nowrap',
          }}
        >
          ⬇ Export CSV
        </button>

        {/* History toggle — only on CLOSED tab */}
        {tab === 'CLOSED' && (
          <button
            onClick={() => setShowHistory(h => !h)}
            style={{
              marginLeft: 8,
              padding: '5px 12px',
              borderRadius: 6,
              border: `1px solid ${showHistory ? 'rgba(99,102,241,0.5)' : 'var(--border)'}`,
              background: showHistory ? 'rgba(99,102,241,0.15)' : 'transparent',
              color: showHistory ? 'var(--accent)' : 'var(--muted)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {showHistory ? '📅 Today only' : '🗂 All history'}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <p style={{ color: 'var(--muted)', padding: 24 }}>Loading...</p>
        ) : positions.length === 0 ? (
          <p style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>No positions found</p>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Symbol</th>
                <th style={thStyle}>Strategy</th>
                <th style={thStyle}>Side</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Entry</th>
                {tab !== 'OPEN'   && <th style={thStyle}>Exit</th>}
                {tab !== 'CLOSED' && <th style={thStyle}>LTP</th>}
                <th style={thStyle}>{tab === 'CLOSED' ? 'Realised P&L' : 'MTM P&L'}</th>
                {tab !== 'CLOSED' && <th style={thStyle}>SL / Target</th>}
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Status</th>
                {tab !== 'CLOSED' && <th style={thStyle}></th>}
              </tr>
            </thead>
            <tbody>
              {positions.map((p, i) => {
                const isOpen   = p.status === 'OPEN';
                const pnl      = p.pl || 0;
                const isBuy    = (p.side || '').toUpperCase() === 'BUY';

                return (
                  <tr
                    key={p.id || i}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: isOpen
                        ? 'transparent'
                        : 'rgba(255,255,255,0.02)',
                      opacity: isOpen ? 1 : 0.72,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = isOpen ? 'transparent' : 'rgba(255,255,255,0.02)'}
                  >
                    {/* Symbol */}
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
                        {p.symbol?.replace(/^(NSE:|BSE:)/, '')}
                      </span>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                        {p.symbol?.startsWith('BSE:') ? 'BSE' : 'NSE'}
                      </div>
                    </td>

                    {/* Strategy */}
                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface)', borderRadius: 4, padding: '2px 6px' }}>
                        {p.strategy || '—'}
                      </span>
                    </td>

                    {/* Side */}
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                        background: isBuy ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
                        color: isBuy ? 'var(--green)' : 'var(--red)',
                        border: `1px solid ${isBuy ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}`,
                      }}>
                        {p.side}
                      </span>
                    </td>

                    {/* Qty */}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{Math.abs(p.qty || p.netQty || 0)}</td>

                    {/* Entry */}
                    <td style={tdStyle}>₹{fmt(p.avgPrice)}</td>

                    {/* Exit (CLOSED / ALL tab) */}
                    {tab !== 'OPEN' && (
                      <td style={tdStyle}>
                        {p.exitPrice != null ? `₹${fmt(p.exitPrice)}` : '—'}
                      </td>
                    )}

                    {/* LTP (OPEN / ALL tab) */}
                    {tab !== 'CLOSED' && (
                      <td style={tdStyle}>
                        {isOpen ? `₹${fmt(p.ltp)}` : '—'}
                      </td>
                    )}

                    {/* P&L */}
                    <td style={{ ...tdStyle, fontWeight: 700, color: pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {pnl >= 0 ? '+' : ''}₹{fmt(pnl)}
                    </td>

                    {/* SL / Target (OPEN / ALL tab) */}
                    {tab !== 'CLOSED' && (
                      <td style={{ ...tdStyle, fontSize: 12 }}>
                        {isOpen ? (
                          <>
                            {p.trailingStopLoss != null ? (
                              <span title={`Initial SL: ₹${fmt(p.stopLoss)}`}>
                                <span style={{ color: 'var(--red)' }}>TSL {fmt(p.trailingStopLoss)}</span>
                                <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 3 }}>▲</span>
                              </span>
                            ) : (
                              <span style={{ color: 'var(--red)' }}>SL {fmt(p.stopLoss)}</span>
                            )}
                            <span style={{ color: 'var(--muted)', margin: '0 4px' }}>/</span>
                            <span style={{ color: 'var(--green)' }}>T {fmt(p.target)}</span>
                          </>
                        ) : '—'}
                      </td>
                    )}

                    {/* Time */}
                    <td style={{ ...tdStyle, fontSize: 12, color: 'var(--muted)' }}>
                      {isOpen ? (
                        <>{dateStr(p.entryTime)}<br />{timeStr(p.entryTime)}</>
                      ) : (
                        <>{dateStr(p.exitTime || p.entryTime)}<br />{timeStr(p.exitTime || p.entryTime)}</>
                      )}
                    </td>

                    {/* Status badge */}
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600,
                        background: isOpen ? 'rgba(52,211,153,0.12)' : 'rgba(150,150,150,0.12)',
                        color: isOpen ? 'var(--green)' : 'var(--muted)',
                        border: `1px solid ${isOpen ? 'rgba(52,211,153,0.25)' : 'rgba(150,150,150,0.2)'}`,
                      }}>
                        {isOpen ? '● OPEN' : '✓ CLOSED'}
                      </span>
                    </td>

                    {/* Per-row exit button — OPEN positions only */}
                    {tab !== 'CLOSED' && (
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {isOpen && (() => {
                          const exitKey = p.paper_trade ? p.id : p.symbol;
                          const isExiting = exitingId === exitKey;
                          return (
                            <button
                              onClick={() => handleExitOne(p)}
                              disabled={isExiting}
                              style={{
                                padding: '4px 10px', borderRadius: 5, fontSize: 12,
                                fontWeight: 600, cursor: isExiting ? 'not-allowed' : 'pointer',
                                border: '1px solid rgba(248,113,113,0.4)',
                                background: isExiting ? 'rgba(248,113,113,0.1)' : 'rgba(248,113,113,0.15)',
                                color: 'var(--red)',
                                opacity: isExiting ? 0.6 : 1,
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                              }}
                              onMouseEnter={e => { if (!isExiting) e.target.style.background = 'rgba(248,113,113,0.3)'; }}
                              onMouseLeave={e => { e.target.style.background = isExiting ? 'rgba(248,113,113,0.1)' : 'rgba(248,113,113,0.15)'; }}
                            >
                              {isExiting ? 'Exiting…' : '⏏ Exit'}
                            </button>
                          );
                        })()}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>

            {/* Footer summary row */}
            {positions.length > 1 && (
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface)' }}>
                  <td colSpan={tab === 'ALL' ? 7 : tab === 'OPEN' ? 6 : 6} style={{ ...tdStyle, color: 'var(--muted)', fontSize: 12 }}>
                    {positions.length} position{positions.length !== 1 ? 's' : ''}
                    {tab === 'ALL' && ` (${openPositions.length} open, ${closedPositions.length} closed)`}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: displayedPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {displayedPnl >= 0 ? '+' : ''}₹{displayedPnl.toFixed(2)}
                  </td>
                  <td colSpan={99} />
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 14px',
  fontSize: 13,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};
